import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import {
  HardHat, FileText, Truck, Users, Receipt, Briefcase,
  ClipboardList, TrendingUp, IndianRupee, AlertTriangle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";

const STATUS_COLORS = {
  planning: "bg-blue-500",
  active: "bg-orange-600",
  on_hold: "bg-yellow-500",
  completed: "bg-green-600",
};

function Stat({ label, value, sub, icon: Icon, tid, accent }) {
  return (
    <div
      data-testid={tid}
      className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-400 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">{label}</div>
          <div className="font-heading font-black text-3xl mt-2 tracking-tight text-zinc-900">
            {value}
          </div>
          {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
        </div>
        <div className={`h-9 w-9 flex items-center justify-center rounded-sm ${accent || "bg-zinc-100"}`}>
          <Icon className={`h-4 w-4 ${accent ? "text-white" : "text-zinc-700"}`} />
        </div>
      </div>
    </div>
  );
}

function fmtINR(n) {
  if (!n) return "₹0";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data.slice(0, 5))).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div>
        <PageHeader overline="OVERVIEW" title="Dashboard" description="Loading control room..." />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        overline="OVERVIEW • CONTROL ROOM"
        title="Company at a glance"
        description="Live status of projects, documents, machinery, invoices and workforce."
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat
          tid="stat-active-projects"
          label="Active Projects"
          value={stats.projects.active}
          sub={`${stats.projects.total} total`}
          icon={HardHat}
          accent="bg-orange-600"
        />
        <Stat
          tid="stat-contract-value"
          label="Contract Value"
          value={fmtINR(stats.total_contract_value)}
          sub="Across all projects"
          icon={IndianRupee}
        />
        <Stat
          tid="stat-nhai-projects"
          label="NHAI Projects"
          value={stats.nhai_projects}
          sub={`PWD: ${stats.pwd_projects}`}
          icon={TrendingUp}
        />
        <Stat
          tid="stat-invoices-pending"
          label="Pending Invoices"
          value={stats.invoices_pending}
          sub={`${stats.invoices_overdue} overdue`}
          icon={stats.invoices_overdue > 0 ? AlertTriangle : Receipt}
          accent={stats.invoices_overdue > 0 ? "bg-red-600" : ""}
        />
        <Stat tid="stat-documents" label="Documents" value={stats.documents_total} sub="Indexed in Brain" icon={FileText} />
        <Stat tid="stat-vendors" label="Vendors" value={stats.vendors_total} sub="Registered" icon={Briefcase} />
        <Stat tid="stat-work-orders" label="Work Orders" value={stats.work_orders_total} sub="Issued" icon={ClipboardList} />
        <Stat tid="stat-machinery" label="Machinery" value={stats.machinery_active} sub={`${stats.employees_total} staff active`} icon={Truck} />
      </div>

      {/* Two-column: Recent projects + Doc categories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
            <div>
              <div className="overline">Projects</div>
              <div className="font-heading font-bold text-lg">Recent activity</div>
            </div>
            <Link
              to="/projects"
              data-testid="dashboard-view-all-projects"
              className="text-xs text-orange-600 hover:underline font-medium"
            >
              View all →
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-zinc-200">
                <th className="px-5 py-2 overline">Name</th>
                <th className="px-5 py-2 overline">Type</th>
                <th className="px-5 py-2 overline">Value</th>
                <th className="px-5 py-2 overline">Status</th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-zinc-400 font-mono text-xs">
                    No projects yet. Create your first project.
                  </td>
                </tr>
              ) : (
                projects.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-5 py-3 font-medium">{p.name}</td>
                    <td className="px-5 py-3">
                      <span className="font-mono text-xs px-2 py-0.5 border border-zinc-300 rounded-sm">
                        {p.project_type}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono">{fmtINR(p.contract_value)}</td>
                    <td className="px-5 py-3">
                      <span className="flex items-center gap-2 text-xs">
                        <span className={`h-1.5 w-1.5 rounded-full ${STATUS_COLORS[p.status] || "bg-zinc-400"}`} />
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-200">
            <div className="overline">Documents</div>
            <div className="font-heading font-bold text-lg">By category</div>
          </div>
          <ul className="p-5 space-y-2">
            {Object.entries(stats.documents_by_category).map(([cat, c]) => (
              <li key={cat} className="flex items-center justify-between text-sm">
                <span className="text-zinc-700">{cat}</span>
                <span className="font-mono text-xs px-2 py-0.5 bg-zinc-100 rounded-sm">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
